"use client";

import { Mic, MicOff } from "lucide-react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";
import { getTone } from "@/lib/ui/tones";
import { cn } from "@/lib/utils";
import MicrophoneSelector from "./MicrophoneSelector";

function LevelBars({ audioLevel, className = "h-12" }) {
  return (
    <div className={`flex items-center justify-center gap-1 ${className}`}>
      {[...Array(5)].map((_, i) => {
        const baseHeight = 10;
        const maxHeight = 90;
        const variation = 0.7 + (i % 2) * 0.3;
        const height =
          baseHeight + (maxHeight - baseHeight) * audioLevel * variation;

        return (
          <div
            key={i}
            className="w-1.5 bg-action-500 rounded-full transition-all duration-150 ease-out"
            style={{ height: `${Math.max(8, height)}%` }}
          />
        );
      })}
    </div>
  );
}

const TRANSCRIPT_TONE = getTone("action");

function TranscriptPreview({ accumulatedTranscript, partialText, className }) {
  if (!accumulatedTranscript && !partialText) return null;
  return (
    <div
      className={cn(
        "p-4 rounded-xl border overflow-y-auto",
        TRANSCRIPT_TONE.bg,
        TRANSCRIPT_TONE.border,
        className
      )}
    >
      <p className="text-sm text-gray-700">
        {accumulatedTranscript && <span>{accumulatedTranscript}</span>}
        {accumulatedTranscript && partialText && <span> </span>}
        {partialText && <span className="italic">{partialText}</span>}
      </p>
    </div>
  );
}

// Full-screen "Listening..." modal. Clicking outside stops the recording.
function RecordingOverlay({
  stopRecording,
  audioLevel,
  microphoneDeviceName,
  connectionReady,
  showMicSelector,
  switchMicrophone,
  selectedMicDeviceId,
  isSwitchingMic,
  accumulatedTranscript,
  partialText,
}) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] animate-in fade-in duration-300"
      style={{ pointerEvents: "auto" }}
      onMouseDown={(e) => {
        // Click outside closes
        if (e.target === e.currentTarget) {
          stopRecording();
        }
      }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl p-12 max-w-md mx-4 text-center"
        style={{ pointerEvents: "auto" }}
        onMouseDown={(e) => {
          // Prevent backdrop click handler
          e.stopPropagation();
        }}
      >
        <div className="relative inline-flex items-center justify-center mb-6">
          <div className="absolute w-32 h-32 bg-action-500/20 rounded-full animate-ping" />
          <div
            className="absolute w-24 h-24 bg-action-500/30 rounded-full animate-ping"
            style={{ animationDelay: "0.2s" }}
          />
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopRecording();
            }}
            className="relative w-20 h-20 bg-gradient-to-br from-action-500 to-action-600 rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl hover:scale-105 transition-transform cursor-pointer"
            style={{ pointerEvents: "auto" }}
          >
            <MicOff className="w-10 h-10 text-white" />
          </button>
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-2">Listening...</h3>
        <p className="text-sm text-gray-500 mb-6 flex items-center justify-center gap-2">
          <Mic className="w-4 h-4" />
          {microphoneDeviceName || "Microphone"}
        </p>

        {!connectionReady && (
          <p className="text-sm text-gray-500 mb-6 flex items-center justify-center gap-2">
            Connecting to transcription…
          </p>
        )}

        {/* In compact mode (like DEM editor), the selector is hidden outside the modal.
            Provide it here so users can recover if a non-mic device is selected. */}
        {showMicSelector && (
          <div className="mb-6 flex justify-center">
            <MicrophoneSelector
              onMicrophoneSelect={switchMicrophone}
              selectedDeviceId={selectedMicDeviceId}
            />
          </div>
        )}

        {isSwitchingMic && (
          <p className="text-xs text-gray-500 mb-4">Switching microphone…</p>
        )}

        <LevelBars audioLevel={audioLevel} className="h-12 mb-6" />

        <TranscriptPreview
          accumulatedTranscript={accumulatedTranscript}
          partialText={partialText}
          className="mb-6 max-h-32"
        />

        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            stopRecording();
          }}
          className="w-full py-3 px-6 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 font-semibold rounded-xl transition-colors cursor-pointer"
          style={{ pointerEvents: "auto" }}
        >
          Stop (& edit text if you wish)
        </button>

        <p className="text-xs text-gray-400 mt-4">
          Click the microphone or this button to stop
        </p>
      </div>
    </div>
  );
}

// Non-modal bar docked to the bottom of the screen. The page behind it stays
// readable and clickable, so a rep can dictate while reading a guide (the
// Deal Clinic discovery tracks). Stop ends the recording; Escape is handled
// by VoiceCommentInput for both layouts, so the panel must not add its own
// (two listeners would run the stop pipeline twice).
function RecordingDock({
  stopRecording,
  audioLevel,
  microphoneDeviceName,
  connectionReady,
  showMicSelector,
  switchMicrophone,
  selectedMicDeviceId,
  isSwitchingMic,
  accumulatedTranscript,
  partialText,
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="voice-recording-dock"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] max-w-xl bg-white border border-gray-200 rounded-xl shadow-lg p-4 space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="flex items-center gap-3">
        <span className="relative flex h-3 w-3 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-action-500/40 animate-ping" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-action-600" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">
            {connectionReady ? "Listening..." : "Connecting to transcription…"}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {isSwitchingMic
              ? "Switching microphone…"
              : microphoneDeviceName || "Microphone"}
          </p>
        </div>
        <LevelBars audioLevel={audioLevel} className="h-8 shrink-0" />
        <Button type="button" size="sm" onClick={stopRecording}>
          <MicOff className="w-4 h-4 mr-1.5" />
          Stop
        </Button>
      </div>

      {showMicSelector && (
        <MicrophoneSelector
          onMicrophoneSelect={switchMicrophone}
          selectedDeviceId={selectedMicDeviceId}
        />
      )}

      <TranscriptPreview
        accumulatedTranscript={accumulatedTranscript}
        partialText={partialText}
        className="max-h-24 p-3"
      />
    </div>
  );
}

export default function RecordingPanel({ layout = "overlay", ...props }) {
  if (typeof document === "undefined") return null;
  const Panel = layout === "dock" ? RecordingDock : RecordingOverlay;
  return createPortal(<Panel {...props} />, document.body);
}
